import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  CardActions,
  CardContent,
  CardHeader,
  Dialog,
  FormControl,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import { useCallback, useEffect, useState } from "react";
import { TYPE_NOTIFICATION } from "../../../../components/reusable/NotificationBull";
import StandardSkeletoon from "../../../../components/reusable/loader/StandardSkeletoon";
import { useQuery } from "../../../../hooks/useQuery";
import useToast from "../../../../hooks/useToast";
import { LocalisationService } from "../../../../services/candidature/LocalisationService";

const INIT_LOCALISATION_INFO = {
  id_choix_localisation: "0",
  nom_lot: "",
};

const INIT_FORM_ERROR = {
  nom_lot_error: null,
};

const LocalisationDialogForm = ({
  open,
  onFormClose,
  onFormOpen,
  id_choix_localisation,
  id_type,
}) => {
  const [localisationInfo, setLocalisationInfo] = useState(
    INIT_LOCALISATION_INFO,
  );
  const [formErrors, setFormErrors] = useState(INIT_FORM_ERROR);
  const [submited, setSubmited] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const { Toast, showToast } = useToast();
  const url = useQuery();
  const idType = url.get("id_type");

  const handleFormClose = () => {
    if (onFormClose) onFormClose();
    setLocalisationInfo(INIT_LOCALISATION_INFO);
  };

  const handleChangeFormInfo = (e) => {
    const { name, value } = e.target;
    setLocalisationInfo({ ...localisationInfo, [name]: value });
  };

  const handleFetchLocalisationInfo = useCallback(() => {
    if(!id_choix_localisation) return;
    setFormLoading(true);
    LocalisationService.find_one({
      id_choix_localisation: id_choix_localisation,
    })
      .then((res) => {
        if (res && res.data) {
          setLocalisationInfo(res.data);
        }
      })
      .finally(() => {
        setFormLoading(false);
      });
  }, [id_choix_localisation]);

  const handleCheckDataValidation = useCallback(() => {
    const nextErrors = {
      nom_lot_error: localisationInfo.nom_lot
        ? null
        : "Nom du lot est obligatoire",
    };
    setFormErrors(nextErrors);
    return Object.values(nextErrors).every((error) => error === null);
  }, [localisationInfo.nom_lot]);

  const handleSave = () => {
    if (id_choix_localisation) {
      handleUpdate();
    } else {
      handleInsert();
    }
  };
  const handleInsert = () => {
    setSubmited(true);
    setLoading(true);
    LocalisationService.insert({ ...localisationInfo, id_choix_localisation: id_choix_localisation })
      .then((res) => {
        if (res && res.success) {
          showToast(TYPE_NOTIFICATION.SUCCESS, res.message);
          handleFormClose();
        } else {
          showToast(TYPE_NOTIFICATION.ERROR, res.message);
        }
      })
      .finally(() => {
        setSubmited(false);
        setLoading(false);
      });
  };

  const handleUpdate = () => {
    setSubmited(true);
    setLoading(true);
    LocalisationService.update({ ...localisationInfo, id_choix_localisation: id_choix_localisation, id_type: id_type })
      .then((res) => {
        if (res && res.success) {
          showToast(TYPE_NOTIFICATION.SUCCESS, res.message);
          handleFormClose();
        } else {
          showToast(TYPE_NOTIFICATION.ERROR, "Erreur lors de la modification de la localisation");
        }
      })
      .finally(() => {
        setSubmited(false);
        setLoading(false);
      });
  };

  const handleSubmit = () => {
    setSubmited(true);
    if (handleCheckDataValidation()) {
      handleSave();
    } else {
      showToast(TYPE_NOTIFICATION.ERROR, "Veuillez verifier les champs");
    }
  };

  useEffect(() => {
    if (localisationInfo.nom_lot) {
      handleCheckDataValidation();
    }
  }, [handleCheckDataValidation, localisationInfo.nom_lot]);

  useEffect(() => {
    setLocalisationInfo((prevLocalisationInfo) => ({
      ...prevLocalisationInfo,
      id_type: idType,
    }));
    if (id_choix_localisation || open) {
      handleFetchLocalisationInfo();
    }
  }, [handleFetchLocalisationInfo, idType, id_choix_localisation, open]);

  return (
    <>
      <Dialog open={open} onClose={handleFormClose} fullWidth maxWidth="sm">
        <Toast />
        <Grid>
          <StandardSkeletoon escalier={true} m={4} row={4} show={formLoading} />
        </Grid>
        <Paper
          sx={{
            display: formLoading ? "none" : "block",
            borderRadius: 2,
            overflow: "hidden",
          }}
          elevation={0}
        >
          <Grid>
            <CardHeader
              sx={{ pb: 1 }}
              title={
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "primary.main",
                      backgroundColor: "#EAF2FF",
                    }}
                  >
                    <PlaceOutlinedIcon />
                  </Box>
                  <Box>
                    <Typography variant="h6" fontWeight={800}>
                      {id_choix_localisation
                        ? "Modifier la localisation"
                        : "Créer une localisation"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Renseignez le nom du lot ou de la zone concernée.
                    </Typography>
                  </Box>
                </Stack>
              }
            />
            <CardContent sx={{ pt: 1 }}>
              <Grid container>
                <FormControl fullWidth>
                  <TextField
                    multiline
                    minRows={3}
                    id="nom_lot"
                    label="Localisation"
                    name="nom_lot"
                    onChange={handleChangeFormInfo}
                    value={localisationInfo.nom_lot || ""}
                    error={formErrors.nom_lot_error && submited}
                    helperText={submited ? formErrors.nom_lot_error : ""}
                    placeholder="Insérer ici la localisation"
                    fullWidth
                    aria-describedby="question_helper"
                  />
                </FormControl>
              </Grid>
            </CardContent>
            <CardActions
              sx={{
                justifyContent: "flex-end",
                px: 3,
                pb: 3,
                pt: 0,
                gap: 1,
              }}
            >
              <Button
                title={"Fermer"}
                size={"medium"}
                variant={"outlined"}
                onClick={(e) => handleFormClose()}
              >
                Fermer
              </Button>
              <LoadingButton
                loading={loading}
                title={"Enregistrer"}
                variant={"contained"}
                size={"medium"}
                onClick={(e) => handleSubmit()}
              >
                Enregistrer
              </LoadingButton>
            </CardActions>
          </Grid>
        </Paper>
      </Dialog>
    </>
  );
};

export default LocalisationDialogForm;
